import * as React from "react";
import classnames from "classnames";
import { debounce } from "lodash";

import { SlackEventShape } from "web_modules/Events/types";

import Fields from "./Fields";
import { formatTime, titleToIcon } from "../shared";

const FontAwesome = require("react-fontawesome");

const styles = require("../styles.module.scss");

interface Props {
  appId: number;
  event: SlackEventShape;
  expanded: boolean;
  onClick: any;
}

const titleLinkIsDeep = (l: string, appId: number): boolean => {
  return !l.endsWith(`${appId}`) && !l.endsWith(`${appId}/`); // e.g., "example.com/underwriter/app/123"
};

class SlackEvent extends React.Component<Props, any> {
  constructor(props) {
    super(props);

    this.state = {
      mounted: false,
      overflowed: null,
    };

    this.descText = React.createRef();
    this.clearDescTextOverflowedFlagToInitiateRetest = debounce(
      this.clearDescTextOverflowedFlagToInitiateRetest,
      250,
    );
  }

  descText = null;

  clearDescTextOverflowedFlagToInitiateRetest = () => {
    this.setState(prevState => ({
      ...prevState,
      overflowed: null,
    }));
  };

  componentDidMount() {
    window.addEventListener("resize", this.clearDescTextOverflowedFlagToInitiateRetest);

    this.setState(prevState => ({
      ...prevState,
      mounted: true,
    }));
  }

  componentDidUpdate() {
    const { current: elem } = this.descText;

    if (this.state.overflowed === null && this.descText.current) {
      this.setState(prevState => ({
        ...prevState,
        overflowed: elem.clientWidth < elem.scrollWidth,
      }));
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.clearDescTextOverflowedFlagToInitiateRetest);
  }

  render() {
    const { appId, event, expanded, onClick } = this.props;
    const { overflowed } = this.state;

    const eventClasses = classnames({
      [styles.event]: true,
      [styles.engItem]: event.notify_scope === "eng",
    });

    let title;
    if (
      event.attachment_title &&
      event.attachment_title_link &&
      titleLinkIsDeep(event.attachment_title_link, appId)
    ) {
      title = (
        <a href={event.attachment_title_link} target="_blank">
          {event.attachment_title}
        </a>
      );
    } else if (event.attachment_title) {
      title = event.attachment_title;
    } else {
      title = <span className={styles.untitled}>—</span>;
    }

    const descriptionClasses = classnames({
      [styles.description]: true,
      [styles.collapsed]: !expanded,
    });

    let text;
    if (event.attachment_text) {
      text = event.attachment_text;
    } else if (event.attachment_fields && !expanded) {
      text = <span className={styles.ellipsis}>More details…</span>;
    } else if (event.attachment_fields) {
      text = null;
    }

    let showToggle;
    if (!this.descText.current) {
      showToggle = false;
    } else {
      showToggle = overflowed || !!event.attachment_fields;
    }

    const toggleClasses = classnames({
      [styles.toggle]: true,
      [styles.hidden]: !showToggle,
    });

    return (
      <li className={eventClasses}>
        <div className={styles.timestamp}>{formatTime(event.created_at)}</div>
        <div className={styles.title}>
          <FontAwesome
            className={styles.icon}
            name={titleToIcon(event.attachment_title)}
            fixedWidth
          />
          {event.notify_scope === "eng" ? `[ENG] ${title}` : title}
        </div>
        <div className={descriptionClasses}>
          <div ref={this.descText}>{text}</div>
          {event.attachment_fields && expanded && <Fields fields={event.attachment_fields} />}
        </div>
        <button type="button" className={toggleClasses} onClick={onClick}>
          <FontAwesome
            name={expanded ? "chevron-circle-down" : "chevron-circle-right"}
            fixedWidth
          />
        </button>
      </li>
    );
  }
}

export default SlackEvent;
